import { LOGIN_COMPLETE, LOGOUT } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';
import { selectLocale, fetchBalance, accountHydrate } from '~/components/account/ducks/index.js';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { errorHandler, createApiAction } from '~/services/middleware/api-middleware';
import currencyFormatter from '../../../../libs/currency-formatter.js';
import { basicApiErrorHandler, errorActionCreatorMap, defaultErrorActionCreator } from './we-spin-error-handler.js';
import REDUCER_NAME from './we-spin-reducer-name.js';
import {
	selectParticipations,
	selectSessions,
	selectSessionForStreamRef,
	selectProfileFetched,
	selectUpcomingSessionsToShowNotificationFor,
	selectIsAdNotificationsEnabled,
	selectPrivateSessions
} from './we-spin-selectors.js';
import { SESSION_SORT_ORDER, ANNOUNCED } from '../constants/we-spin-stream-states.js';
import { FREE_WESPIN } from '../constants/we-spin-boost-constants.js';
import { showNotification } from '../../../notifications/ducks/notifications.js';
import { checkIsSessionActive } from '../utils/we-spin-time-helper.js';
import { FETCH_WESPIN_DESKTOP_GAMES_FINISH, FETCH_WESPIN_DESKTOP_GAMES_START } from './we-spin-games-duck';

import { showWebPushPrompt } from '../../../web-push/ducks/web-push.js';

import { selectWebPushEnabled, selectWebPushTriggerWeSpinSuccess } from '../../../brand/ducks/selectors.js';
import { WE_SPIN_CHANGE_USERNAME_NOTIFICATION_SUCCESS_INFO } from '../constants/we-spin-text-keys';

function isStreamerLiveForSession(session) {
	const isSessionActive = checkIsSessionActive(session);
	if (isSessionActive) return Boolean(session?.live);
	return false;
}

const WE_SPIN_COOKIE_ENABLE_WE_SPIN_TEST_STREAMERS = 'enableWeSpinTestStreamers';
const AD_NOTIFICATIONS_POLLING_INTERVAL = 60000;
const WE_SPIN_BASE_URL = '/we-spin-api';
const ACTION_PREFIX = '@komigen/we-spin';

// Events
export const FETCH_SESSIONS = `${ACTION_PREFIX}/FETCH_SESSIONS`;
export const FETCH_WESPIN_BALANCE = `${ACTION_PREFIX}/FETCH_WESPIN_BALANCE`;
export const FETCH_GAME_VIEW_EVENT = `${ACTION_PREFIX}/FETCH_GAME_VIEW_EVENT`;
export const FETCH_GAME_ROOM_PLAYERS = `${ACTION_PREFIX}/FETCH_GAME_ROOM_PLAYERS`;
const FETCH_SESSIONS_COMPLETED = `${ACTION_PREFIX}/FETCH_SESSIONS_COMPLETED`;
const FETCH_PRIVATE_SESSIONS_COMPLETED = `${ACTION_PREFIX}/FETCH_PRIVATE_SESSIONS_COMPLETED`;
const RESET_PRIVATE_SESSIONS = `${ACTION_PREFIX}/RESET_PRIVATE_SESSIONS`;
export const FETCH_GAME_ROOM = `${ACTION_PREFIX}/FETCH_GAME_ROOM`;
const FETCH_GAME_ROOM_COMPLETED = `${ACTION_PREFIX}/FETCH_GAME_ROOM_COMPLETED`;
const FETCH_FINISH_GAME = `${ACTION_PREFIX}/FETCH_FINISH_GAME`;
const FETCH_PROFILE = `${ACTION_PREFIX}/FETCH_PROFILE`;
const FETCHED_PROFILE = `${ACTION_PREFIX}/FETCHED_PROFILE`;
const SET_TEMPORARY_BUY_IN = `${ACTION_PREFIX}/SET_TEMPORARY_BUY_IN`;
const FETCH_HAS_MADE_DEPOSIT = `${ACTION_PREFIX}/FETCH_HAS_MADE_DEPOSIT`;
const SET_CURRENT_STREAMVIEW = `${ACTION_PREFIX}/SET_CURRENT_STREAMVIEW`;

const FETCH_PARTICIPATIONS_COMPLETED = `${ACTION_PREFIX}/FETCH_PARTICIPATIONS_COMPLETED`;
const SET_PLAYER_BALANCE_IN_EVENT_CURRENCY = `${ACTION_PREFIX}/SET_PLAYER_BALANCE_IN_EVENT_CURRENCY`;

const SET_PLAYERS_BOUGHT_INTO_SESSION = `${ACTION_PREFIX}/SET_PLAYERS_BOUGHT_INTO_SESSION`;

const SET_AD_NOTIFICATIONS_ENABLED = `${ACTION_PREFIX}/SET_AD_NOTIFICATIONS_ENABLED`;
const ADD_REF_TO_NOTIFICATIONS_SEEN_BY_PLAYER = `${ACTION_PREFIX}/ADD_REF_TO_NOTIFICATIONS_SEEN_BY_PLAYER`;

const FETCH_CLIPS = `${ACTION_PREFIX}/FETCH_CLIPS`;
const HYDRATE_CONF_AND_CAROUSEL = `${ACTION_PREFIX}/HYDRATE_CONF_AND_CAROUSEL`;
export const FETCH_TWITCH_AUTH = `${ACTION_PREFIX}/FETCH_TWITCH_AUTH`;

export const addRefToNotificationsSeenByPlayer = sessionRef => ({
	type: ADD_REF_TO_NOTIFICATIONS_SEEN_BY_PLAYER,
	payload: sessionRef
});

const setParticipationsActionCreator = participations => ({
	type: FETCH_PARTICIPATIONS_COMPLETED,
	payload: participations
});

const sessionSorter = (
	{ estimatedStartTime: timeA, sessionState: stateA },
	{ estimatedStartTime: timeB, sessionState: stateB }
) => (stateA === stateB ? new Date(timeA) - new Date(timeB) : SESSION_SORT_ORDER[stateA] - SESSION_SORT_ORDER[stateB]);

const transformReadSessions = sessions =>
	sessions
		.map(({ streamViews, ...session }) => ({
			...session,
			streamViews: streamViews.map(streamView => ({
				...streamView,
				balanceCapMet:
					streamView.boost?.type !== FREE_WESPIN && // Filter out free wespins, because it's balance cap will always be met
					streamView.balanceCap <= streamView.balance + streamView.buyinAmounts[0],
				playerCapMet:
					streamView.boost?.noPlayers &&
					streamView.boost.noPlayers !== 0 &&
					streamView.boost.noPlayers <= streamView.noParticipants,
				isFreeWeSpin: streamView.boost?.type === FREE_WESPIN,
				isEventDisabled: streamView.boost?.type === FREE_WESPIN && streamView.state === ANNOUNCED
			}))
		}))
		.map(session => ({
			...session,
			live: isStreamerLiveForSession(session),
			joined: session.streamViews.some(({ joined }) => Boolean(joined)),
			balanceCapMet: session.streamViews.every(({ balanceCapMet }) => Boolean(balanceCapMet)),
			buyInAmount: session.streamViews.reduce((acc, { buyInAmount = 0 }) => acc + buyInAmount, 0),
			playerCapMet: session.streamViews.every(({ playerCapMet }) => Boolean(playerCapMet))
		}))
		.sort(sessionSorter);

const setSessions = sessions => ({
	type: FETCH_SESSIONS_COMPLETED,
	payload: {
		sessions
	}
});

const resetPrivateSessions = () => ({
	type: RESET_PRIVATE_SESSIONS
});

const setPrivateSessions = privateSessions => ({
	type: FETCH_PRIVATE_SESSIONS_COMPLETED,
	payload: {
		privateSessions
	}
});

const updateSession = session => (dispatch, getState) => {
	// clone array and exchange the session
	let sessions = selectSessions(getState());
	if (sessions.some(s => s.sessionRef === session.sessionRef)) {
		sessions = sessions.map(s => (s.sessionRef === session.sessionRef ? session : s));
		const transSession = transformReadSessions(sessions);
		return dispatch(setSessions(transSession));
	}
	let privateSessions = selectPrivateSessions(getState());
	if (privateSessions.some(s => s.sessionRef === session.sessionRef)) {
		privateSessions = privateSessions.map(s => (s.sessionRef === session.sessionRef ? session : s));
		const transPrivateSessions = transformReadSessions(privateSessions);
		return dispatch(setPrivateSessions(transPrivateSessions));
	}
};

const EMPTY_ARRAY = [];
export const fetchParticipations =
	({ forcedUpdate = false } = {}) =>
	async (dispatch, getState) => {
		const state = getState();
		const authenticated = authenticationSelector(state);
		const participations = selectParticipations(state);
		if (authenticated && (forcedUpdate || participations === EMPTY_ARRAY)) {
			return dispatch(
				createApiAction({
					endpoint: `${WE_SPIN_BASE_URL}/spectator/participations`,
					onSuccess: setParticipationsActionCreator,
					onError: basicApiErrorHandler
				})
			);
		}
		if (!authenticated) {
			return dispatch(setParticipationsActionCreator(EMPTY_ARRAY));
		}
	};

const createStreamViewsWithBuyIns = (streamViews, participations) =>
	streamViews.map(streamView => {
		const buyIn = participations.find(({ streamRef }) => streamView.streamRef === streamRef);
		return {
			...streamView,
			joined: buyIn !== undefined,
			buyInAmount: buyIn && buyIn.buyIn
		};
	});

export const fetchSessions = fetchParticipationOptions => async dispatch => {
	await dispatch(fetchParticipations(fetchParticipationOptions));

	let url = `${WE_SPIN_BASE_URL}/view/sessions`;

	const includeTestStreamers = document.cookie.indexOf(`${WE_SPIN_COOKIE_ENABLE_WE_SPIN_TEST_STREAMERS}=true`) > -1;

	if (includeTestStreamers) {
		url += '?includeTestStreamers=true';
	}

	// Make the API dispatch
	return dispatch(
		createApiAction({
			endpoint: url,
			onSuccess: sessions => (_dispatch, getState) => {
				const state = getState();
				const participations = selectParticipations(state);
				const sessionsWithBuyIns = sessions.map(session => ({
					...session,
					live: isStreamerLiveForSession(session),
					streamViews: createStreamViewsWithBuyIns(session.streamViews, participations)
				}));
				const transSessionsWithBuyIns = transformReadSessions(sessionsWithBuyIns);
				dispatch(setSessions(transSessionsWithBuyIns));
			}
		})
	);
};

export const fetchPrivateSessions = () => async (dispatch, getState) => {
	const url = `${WE_SPIN_BASE_URL}/view/sessions/private`;
	const authenticated = authenticationSelector(getState());
	if (!authenticated) {
		return dispatch(resetPrivateSessions());
	}
	// Make the API dispatch if authenticated
	return dispatch(
		createApiAction({
			endpoint: url,
			onSuccess: sessions => () => {
				const state = getState();
				const participations = selectParticipations(state);
				const sessionsWithBuyIns = sessions.map(session => ({
					...session,
					live: isStreamerLiveForSession(session),
					streamViews: createStreamViewsWithBuyIns(session.streamViews, participations)
				}));
				dispatch(setPrivateSessions(sessionsWithBuyIns));
			}
		})
	);
};

export const spectatorUnregister = streamRef => {
	// let isPrivateSession = false;
	return createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/spectator/unregister`,
		method: 'POST',
		payload: {
			streamRef
		},
		onStart: () => (dispatch, getState) => {
			const state = getState();
			// pretend all went well
			dispatch(
				setParticipationsActionCreator(selectParticipations(state).filter(item => item.streamRef !== streamRef))
			);
		},
		onSuccess: () => dispatch => {
			dispatch(fetchBalance());
		},
		onError: basicApiErrorHandler,
		onFinally: () => dispatch => {
			dispatch(fetchPrivateSessions());
			dispatch(fetchSessions({ forcedUpdate: true }));
		}
	});
};

export const spectatorTip = (streamRef, amount, message = '') =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/spectator/tip`,
		method: 'POST',
		payload: {
			streamRef,
			amount,
			message
		},
		onError: basicApiErrorHandler
	});

const hasMadeDepositActionCreator = hasMadeDeposit => ({
	type: FETCH_HAS_MADE_DEPOSIT,
	payload: {
		hasMadeDeposit
	}
});

const setDisplayNameActionCreator = displayName => ({
	type: FETCH_PROFILE,
	payload: {
		displayName
	}
});

export const fetchProfile = () => (dispatch, getState) => {
	const state = getState();
	const authenticated = authenticationSelector(state);
	const profileFetched = selectProfileFetched(state);
	if (authenticated && !profileFetched) {
		dispatch(
			createApiAction({
				endpoint: '/rest/bonus/hasPlayerMadeDeposit',
				onSuccess: ({ result }) => hasMadeDepositActionCreator(result)
			})
		);
		dispatch(
			createApiAction({
				endpoint: `${WE_SPIN_BASE_URL}/spectator/profile`,
				onSuccess: ({ displayName }) => setDisplayNameActionCreator(displayName),
				onFinally: () => ({
					type: FETCHED_PROFILE
				})
			})
		);
	}
};

export const fetchHasMadeDeposit = () => (dispatch, getState) => {
	const state = getState();
	const authenticated = authenticationSelector(state);
	if (authenticated) {
		return dispatch(
			createApiAction({
				endpoint: '/rest/bonus/hasPlayerMadeDeposit',
				onSuccess: ({ result }) => hasMadeDepositActionCreator(result)
			})
		);
	}
};
const setState = payload => ({
	type: HYDRATE_CONF_AND_CAROUSEL,
	payload
});

export const hydrateConfiguration = () =>
	createApiAction({
		endpoint: `/rest/state/we-spin`,
		onSuccess: ({ result }) => setState(result)
	});

export const setupWeSpinLiveEventSessionId = () =>
	createApiAction({
		method: 'POST',
		endpoint: `${WE_SPIN_BASE_URL}/streamer/takeMeToEvent`,
		onSuccess: () => dispatch => {
			// Hydrate count after cookie sessionId changed
			dispatch(fetchProfile());
			dispatch(accountHydrate());
		}
	});

const setCurrentGameRoom = gameRoom => ({
	type: FETCH_GAME_ROOM_COMPLETED,
	payload: {
		currentGameRoom: gameRoom
	}
});

export const fetchLiveStreamData = streamRef =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/view/liveStreamData?streamRef=${streamRef}`,
		onSuccess: setCurrentGameRoom,
		onError: basicApiErrorHandler
	});

const setFinishGameActionCreator =
	({ yourBalance, yourBuyIn, currency, result, streamRef }) =>
	(dispatch, getState) => {
		const locale = selectLocale(getState());
		const endBalanceFormatted = currencyFormatter.format(yourBalance, currency, locale);

		const myBuyInFormatted = currencyFormatter.format(yourBuyIn, currency, locale);

		const netResultFormatted = currencyFormatter.format(result, currency, locale);

		return dispatch({
			type: FETCH_FINISH_GAME,
			payload: {
				yourBalance: endBalanceFormatted,
				myBuyIn: myBuyInFormatted,
				netResult: netResultFormatted,
				hasWon: result > 0,
				streamRef
			}
		});
	};

export const fetchFinishGame = streamRef =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/spectator/finishedGame?streamRef=${streamRef}`,
		onSuccess: setFinishGameActionCreator,
		onError: basicApiErrorHandler
	});

const setClips = data => ({
	type: FETCH_CLIPS,
	payload: data
});

export const updateDisplayName = displayName =>
	createApiAction({
		method: 'POST',
		endpoint: `${WE_SPIN_BASE_URL}/spectator/updateDisplayName`,
		payload: { displayName },
		onError: e => dispatch => {
			if (e === 420) {
				dispatch(
					showNotification({
						openInSidebar: true,
						color: 'red',
						autoHide: true,
						message: `Maybe this username is already taken. Try another one please.`
					})
				);
			} else {
				dispatch(basicApiErrorHandler(e));
			}
		},
		onSuccess: () => dispatch => {
			dispatch(setDisplayNameActionCreator(displayName));
			dispatch(
				showNotification({
					openInSidebar: true,
					color: 'green',
					autoHide: true,
					messageKey: WE_SPIN_CHANGE_USERNAME_NOTIFICATION_SUCCESS_INFO
				})
			);
		}
	});

const spectatorErrorHandler = ({ overBalanceCapErrorHandler, buyInTooLowErrorHandler, balanceTooLowErrorHandler }) =>
	errorHandler({
		errorActionCreatorMap: {
			...errorActionCreatorMap,
			412:
				(
					// Override 412 for 'over balance cap' and 'buy-in too low' errors
					errorStatus,
					errorStatusText,
					errorResponse
				) =>
				async dispatch => {
					let error;
					try {
						error = await errorResponse.json();
					} catch (e) {
						// nvm
					}
					if (error && error.message) {
						const msg = error.message.toLowerCase();
						if (overBalanceCapErrorHandler && msg.startsWith('over balance cap'))
							overBalanceCapErrorHandler(error);
						else if (buyInTooLowErrorHandler && msg.startsWith('buy-in too low'))
							buyInTooLowErrorHandler(error);
						else if (balanceTooLowErrorHandler && msg.startsWith('balance too low'))
							balanceTooLowErrorHandler(error);
						return error;
					}
					return dispatch(errorActionCreatorMap[412](errorStatus, errorStatusText, errorResponse));
				}
		},
		defaultErrorActionCreator
	});

export const updateBuyIn = (
	streamRef,
	amount,
	overBalanceCapErrorHandler,
	buyInTooLowErrorHandler,
	balanceTooLowErrorHandler
) =>
	createApiAction({
		method: 'POST',
		endpoint: `${WE_SPIN_BASE_URL}/spectator/updateBuyIn`,
		payload: { streamRef, amount },
		onError: spectatorErrorHandler({
			overBalanceCapErrorHandler,
			buyInTooLowErrorHandler,
			balanceTooLowErrorHandler
		}),
		onSuccess: () => dispatch => {
			dispatch(fetchBalance());
		},
		onFinally: () => fetchSessions({ forcedUpdate: true })
	});

export const setTemporaryBuyIn = (amount, streamRef) => ({
	type: SET_TEMPORARY_BUY_IN,
	payload: {
		streamRef,
		amount
	}
});

export const spectatorRegister = (
	amount,
	streamRef,
	overBalanceCapErrorHandler,
	buyInTooLowErrorHandler,
	balanceTooLowErrorHandler,
	sendReminder
) =>
	createApiAction({
		method: 'POST',
		endpoint: `${WE_SPIN_BASE_URL}/spectator/register`,
		payload: {
			amount,
			streamRef,
			shouldSendReminder: sendReminder
		},
		onError: spectatorErrorHandler({
			overBalanceCapErrorHandler,
			buyInTooLowErrorHandler,
			balanceTooLowErrorHandler
		}),
		onSuccess: () => (dispatch, getState) => {
			// update stream to reflect the successful buy-in immediately
			const state = getState();
			const participations = selectParticipations(state);
			const amountAsNumber = Number(amount);

			const webPushEnabled = selectWebPushEnabled(state);
			const webPushTriggerEnabled = selectWebPushTriggerWeSpinSuccess(state);

			dispatch(
				setParticipationsActionCreator(
					participations.filter(p => p.streamRef !== streamRef).concat({ streamRef, buyIn: amountAsNumber })
				)
			);
			const session = selectSessionForStreamRef(streamRef)(state);
			if (session) {
				dispatch(
					updateSession({
						...session,
						joined: true,
						noParticipants: session.noParticipants + 1,
						totalBalance: session.totalBalance + amountAsNumber,
						streamViews: session.streamViews.map(streamView => {
							if (streamView.streamRef !== streamRef) return streamView;
							return {
								...streamView,
								joined: true,
								buyInAmount: amountAsNumber,
								balance: streamView.balance + amountAsNumber
							};
						})
					})
				);
			}
			dispatch(setTemporaryBuyIn('', streamRef));
			// Fetch new balance for main header
			dispatch(fetchBalance());

			if (webPushEnabled && webPushTriggerEnabled) {
				// calling the redux action to show the WEB-PUSH prompt
				dispatch(showWebPushPrompt(true, `we-spin-success`));
			}

			dispatch(
				showNotification({
					openInSidebar: true,
					color: 'green',
					autoHide: true,
					messageKey: 'WE_SPIN_DASHBOARD_BOUGHT_IN_SUCCESS_NOTIFICATION'
				})
			);
		},
		onFinally: () => fetchSessions({ forcedUpdate: true })
	});

export const spectatorRegisterFreeWespin = (amount, streamRef, overBalanceCapErrorHandler, sendReminder) =>
	createApiAction({
		method: 'POST',
		endpoint: `${WE_SPIN_BASE_URL}/spectator/register`,
		payload: {
			amount,
			streamRef,
			shouldSendReminder: sendReminder
		},
		onError: spectatorErrorHandler({ overBalanceCapErrorHandler }),
		onSuccess: () => (dispatch, getState) => {
			const state = getState();
			const participations = selectParticipations(state);
			dispatch(
				setParticipationsActionCreator(
					participations.filter(p => p.streamRef !== streamRef).concat({ streamRef })
				)
			);
			const session = selectSessionForStreamRef(streamRef)(state);
			dispatch(
				updateSession({
					...session,
					joined: true,
					noParticipants: session.noParticipants + 1,
					streamViews: session.streamViews.map(streamView => {
						if (streamView.streamRef !== streamRef) return streamView;
						return {
							...streamView,
							joined: true
						};
					})
				})
			);
			dispatch(setTemporaryBuyIn('', streamRef));
			dispatch(
				showNotification({
					openInSidebar: true,
					color: 'green',
					autoHide: true,
					messageKey: 'WE_SPIN_FREE_WESPIN_JOINED_SUCCESS_NOTIFICATION'
				})
			);
		},
		onFinally: () => fetchSessions({ forcedUpdate: true })
	});

export const setPlayerBalanceInEventCurrency = (amount, streamRef) => ({
	type: SET_PLAYER_BALANCE_IN_EVENT_CURRENCY,
	payload: {
		amount,
		streamRef
	}
});

export const playerBalanceInEventCurrency = streamRef =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/spectator/balanceInEventCurrency?streamRef=${streamRef}`,
		onSuccess: amount => setPlayerBalanceInEventCurrency(amount, streamRef),
		onError: basicApiErrorHandler
	});

export const setPlayersBoughtIntoSession = (players, streamRef) => ({
	type: SET_PLAYERS_BOUGHT_INTO_SESSION,
	payload: {
		players,
		streamRef
	}
});

export const setOnsiteNotificationsEnabled = result => ({
	type: SET_AD_NOTIFICATIONS_ENABLED,
	payload: result
});

export const fetchPlayersBoughtIntoSession = streamRef =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/spectator/screenNamesForStream?streamRef=${streamRef}`,
		onSuccess: players => setPlayersBoughtIntoSession(players, streamRef),
		onError: basicApiErrorHandler
	});

export const setCurrentStreamView = streamView => ({
	type: SET_CURRENT_STREAMVIEW,
	payload: streamView
});

export const fetchAdNotificationsEnabled = () =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/player/onsiteAdNotificationEnabled`,
		onSuccess: result => setOnsiteNotificationsEnabled(result),
		onError: basicApiErrorHandler
	});

export const setOnsiteAdNotificationsEnabled = enabled =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/player/setOnsiteAdNotificationEnabled`,
		method: 'POST',
		payload: {
			enabled
		},
		onError: basicApiErrorHandler
	});

export const fetchClips = () => dispatch =>
	dispatch(
		createApiAction({
			endpoint: `${WE_SPIN_BASE_URL}/view/clips`,
			onSuccess: data => () => dispatch(setClips(data))
		})
	);

export const createClip = ({ url }) =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/streamer/createClip`,
		method: 'POST',
		payload: {
			url
		},
		onError: basicApiErrorHandler,
		onSuccess: () => dispatch => {
			dispatch(
				showNotification({
					openInSidebar: false,
					messageKey: 'WE_SPIN_CLIP_MODAL_SUCCESS',
					color: 'green',
					autoHide: true,
					autoHideDuration: 10000
				})
			);
		}
	});

export const displayAdNotification =
	({ showSidebar, buttonLinkTo, buttonText, messageTextFirst, messageTextSecond, streamerRef }) =>
	dispatch => {
		dispatch(
			showNotification({
				openInSidebar: showSidebar,
				color: 'neutral',
				autoHide: true,
				buttonLinkInternal: buttonLinkTo,
				buttonText,
				message: `${messageTextFirst} ${streamerRef} ${messageTextSecond}`,
				autoHideDuration: 10000
			})
		);
	};

export const pollSessionsAndShowAdNotification = notificationData => async (dispatch, getState) => {
	await dispatch(fetchAdNotificationsEnabled());

	const isAdNotificationsEnabled = selectIsAdNotificationsEnabled(getState());

	if (!isAdNotificationsEnabled) {
		return;
	}

	await dispatch(fetchSessions());

	const upcomingSessions = selectUpcomingSessionsToShowNotificationFor(getState());

	if (upcomingSessions.length > 0) {
		const upcomingSession = upcomingSessions[0];
		const newNotificationData = {
			...notificationData,
			streamerRef: upcomingSession.streamerRef // name of the streamer
		};

		dispatch(displayAdNotification(newNotificationData));
		dispatch(addRefToNotificationsSeenByPlayer(upcomingSession.sessionRef));
	}
};

export const subscribeToAdNotifications = (dispatch, notificationData) => {
	const id = setInterval(() => {
		dispatch(pollSessionsAndShowAdNotification(notificationData));
	}, AD_NOTIFICATIONS_POLLING_INTERVAL);

	return () => clearInterval(id);
};

const initialState = {
	finishGames: {},
	displayName: undefined,
	fetchedProfile: false,
	currentGameRoom: {},
	currentStreamView: {},
	participations: EMPTY_ARRAY,
	sessionsLoaded: false,
	didTip: false,
	temporaryBuyIn: {},
	playerBalanceInEventCurrency: {},
	playersBoughtIntoSession: {},
	sessions: [],
	privateSessions: [],
	carousel: {
		hydrated: false,
		items: null
	},
	isAdNotificationsEnabled: true,
	notificationsSeenByPlayer: [],
	clips: [],
	configuration: false,
	initialized: false,
	availableGames: {
		loading: false,
		hydrated: false,
		gamesList: {},
		providers: {}
	}
};

const reducer = (stateTemp = initialState, { type, payload } = {}) => {
	// merges preloaded state with initial state since we get only partial state from server
	const state = stateTemp.initialized ? stateTemp : { ...initialState, ...stateTemp, initialized: true };
	switch (type) {
		case LOGIN_COMPLETE:
		case LOGOUT:
			return {
				...state,
				participations: EMPTY_ARRAY,
				displayName: undefined,
				fetchedProfile: false
			};

		case FETCH_PARTICIPATIONS_COMPLETED:
			return {
				...state,
				participations: payload
			};
		case FETCH_SESSIONS_COMPLETED:
			return {
				...state,
				sessionsLoaded: true,
				sessions: payload.sessions
			};
		case RESET_PRIVATE_SESSIONS:
			return {
				...state,
				privateSessionsLoaded: false,
				privateSessions: []
			};
		case FETCH_PRIVATE_SESSIONS_COMPLETED:
			return {
				...state,
				privateSessionsLoaded: true,
				privateSessions: payload.privateSessions
			};
		case FETCH_GAME_ROOM_COMPLETED:
			return { ...state, currentGameRoom: payload.currentGameRoom };
		case FETCH_PROFILE:
			return {
				...state,
				displayName: payload.displayName
			};
		case FETCHED_PROFILE:
			return {
				...state,
				fetchedProfile: true
			};
		case FETCH_FINISH_GAME:
			return {
				...state,
				finishGames: {
					...state.finishGames,
					[payload.streamRef]: payload
				}
			};
		case SET_TEMPORARY_BUY_IN:
			return {
				...state,
				temporaryBuyIn: {
					...state.temporaryBuyIn,
					[payload.streamRef]: payload.amount
				}
			};
		case SET_PLAYER_BALANCE_IN_EVENT_CURRENCY:
			return {
				...state,
				playerBalanceInEventCurrency: {
					...state.playerBalanceInEventCurrency,
					[payload.streamRef]: payload.amount
				}
			};
		case SET_PLAYERS_BOUGHT_INTO_SESSION:
			return {
				...state,
				playersBoughtIntoSession: {
					...state.playersBoughtIntoSession,
					[payload.streamRef]: payload.players
				}
			};
		case FETCH_HAS_MADE_DEPOSIT:
			return {
				...state,
				hasMadeDeposit: payload.hasMadeDeposit
			};
		case SET_CURRENT_STREAMVIEW:
			return {
				...state,
				currentStreamView: payload
			};
		case SET_AD_NOTIFICATIONS_ENABLED:
			return {
				...state,
				isAdNotificationsEnabled: payload
			};
		case ADD_REF_TO_NOTIFICATIONS_SEEN_BY_PLAYER:
			return {
				...state,
				notificationsSeenByPlayer: [...state.notificationsSeenByPlayer, payload]
			};
		case FETCH_CLIPS:
			return {
				...state,
				clips: payload
			};
		case HYDRATE_CONF_AND_CAROUSEL:
			return {
				...state,
				configuration: {
					...payload.configuration,
					videoMonitoringEnabledLicenses: payload.configuration?.videoMonitoringEnabledLicenses?.split(',')
				},
				carousel: {
					...payload.carousel,
					hydrated: true
				}
			};
		case FETCH_WESPIN_DESKTOP_GAMES_START:
			return {
				...state,
				availableGames: {
					...state.availableGames,
					loading: true,
					hydrated: false
				}
			};
		case FETCH_WESPIN_DESKTOP_GAMES_FINISH:
			return {
				...state,
				availableGames: {
					loading: false,
					hydrated: true,
					...payload
				}
			};
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);
